import React, { useEffect, useState } from "react";
import axios from "axios";
import "./styles.css";

const API = process.env.REACT_APP_API_URL;
//const API = "http://127.0.0.1:8000";
const N8N_WEBHOOK = "https://aiuhf.app.n8n.cloud/webhook/ebfa427f-ca3a-4587-883c-76d702a9fa46";

function ClientDetails() {
  const [data, setData] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editedRows, setEditedRows] = useState({});
  const [viewRow, setViewRow] = useState(null);
  const [viewRowIdx, setViewRowIdx] = useState(null);
  const [copied, setCopied] = useState(null);
  const [editCell, setEditCell] = useState(null);
  const [editValue, setEditValue] = useState("");

  const token = localStorage.getItem("admin_token");

  const fetchData = async () => {
    setTableLoading(true);
    try {
      const res = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const headers = data[0] ? Object.keys(data[0]) : [];

  const copyToClipboard = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const startEdit = (colKey, currentValue) => {
    setEditCell(colKey);
    setEditValue(currentValue ?? "");
  };

  const cancelEdit = () => { setEditCell(null); setEditValue(""); };

  const confirmEdit = (colKey) => {
    const updated = [...data];
    updated[viewRowIdx] = { ...updated[viewRowIdx], [colKey]: editValue };
    setData(updated);
    setViewRow(updated[viewRowIdx]);
    setEditedRows((prev) => ({
      ...prev,
      [viewRowIdx]: { ...prev[viewRowIdx], [colKey]: editValue },
    }));
    setEditCell(null);
    setEditValue("");
  };

  const saveRow = async (rowIdx) => {
    if (!editedRows[rowIdx]) return;
    setSaving(rowIdx);
    try {
      await axios.put(
        `${API}/clients/update`,
        { row_index: rowIdx, updatedData: data[rowIdx] },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const editedFields = editedRows[rowIdx];
      if (editedFields["Payment Terms"] !== undefined) {
        const contactPerson = data[rowIdx]["Contact Person"];
        const paymentTerms = data[rowIdx]["Payment Terms"];

        const phasesRes = await axios.get(`${API}/phases/by-client`, {
          params: { contact_person: contactPerson },
          headers: { Authorization: `Bearer ${token}` }
        });
        const phases = phasesRes.data.phases;

        const milestonesRes = await axios.get(`${API}/milestones`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const allMilestones = milestonesRes.data;

        const phasesWithMilestones = phases.map(phase => {
          const projectTitle = phase["Project Title"] || "";
          const phaseTitle = phase["Phase Title"] || "";

          const relatedMilestones = allMilestones
            .filter(m =>
              m["Project Title"]?.toString().trim().toLowerCase() === projectTitle.trim().toLowerCase() &&
              m["Project Phases"]?.toString().trim().toLowerCase() === phaseTitle.trim().toLowerCase()
            )
            .map(m => ({
              unique_key: m["Unique Key"],
              milestone_title: m["Milestones Title"],
              milestone_type: m["Milestones Types"],
              expected_date: m["Expected Date"] || "",
            }));

          return {
            project_title: projectTitle,
            phase_title: phaseTitle,
            phase_amount: phase["Phase Amount"] || "",
            tax_per: phase["Tax (%)"] || "",
            milestones: relatedMilestones,
          };
        });

        await axios.post(N8N_WEBHOOK, {
          source: "clients",
          row_index: rowIdx,
          updatedData: data[rowIdx],
          payment_terms: paymentTerms,
          phases: phasesWithMilestones,
          timestamp: new Date().toISOString(),
        });
      } else {
        await axios.post(N8N_WEBHOOK, {
          source: "clients",
          row_index: rowIdx,
          updatedData: data[rowIdx],
          timestamp: new Date().toISOString(),
        });
      }

      setEditedRows((prev) => {
        const copy = { ...prev };
        delete copy[rowIdx];
        return copy;
      });

      alert("✅ Updated in Google Sheet & n8n notified!");
    } catch (err) {
      console.error("Save error:", err.response?.data || err);
      alert("❌ Failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(null);
    }
  };

  const openView = (row, idx) => {
    setViewRow(row);
    setViewRowIdx(idx);
    setEditCell(null);
    setEditValue("");
  };

  const closeView = () => {
    setViewRow(null);
    setViewRowIdx(null);
    setEditCell(null);
    setEditValue("");
  };

  const modalFieldStyle = {
    marginBottom: "10px",
    borderBottom: "1px solid #eee",
    paddingBottom: "10px",
  };

  return (
    <div className="container">
      <h1>📄 Client Details</h1>

      {/* ── View Modal ── */}
      {viewRow && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
          background: "rgba(0,0,0,0.5)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <div style={{
            background: "#fff", borderRadius: "12px", padding: "32px",
            width: "640px", maxWidth: "90%", maxHeight: "85vh",
            overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.2)"
          }}>
            {/* Modal header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ margin: 0, color: "#1a3c6e" }}>👤 Client Details</h2>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                {editedRows[viewRowIdx] && (
                  <button
                    onClick={() => saveRow(viewRowIdx)}
                    disabled={saving === viewRowIdx}
                    style={{
                      background: "#1a3c6e", color: "#fff", border: "none",
                      borderRadius: "8px", padding: "6px 14px", cursor: "pointer",
                      fontSize: "13px", fontWeight: "600"
                    }}
                  >
                    {saving === viewRowIdx ? "⏳ Saving..." : "💾 Save Changes"}
                  </button>
                )}
                <button
                  onClick={closeView}
                  style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: "#666" }}
                >✖</button>
              </div>
            </div>

            {/* All fields except Payment Terms */}
            {Object.entries(viewRow)
              .filter(([key]) => key !== "Payment Terms")
              .map(([key, val]) => {
                const isEditing = editCell === key;
                return (
                  <div key={key} style={modalFieldStyle}>
                    <div style={{ fontSize: "12px", color: "#888", marginBottom: "6px" }}>{key}</div>

                    {isEditing ? (
                      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                        <input
                          style={{
                            flex: 1, padding: "6px 10px", fontSize: "14px",
                            border: "1px solid #1a3c6e", borderRadius: "6px", outline: "none"
                          }}
                          value={editValue}
                          autoFocus
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") confirmEdit(key);
                            if (e.key === "Escape") cancelEdit();
                          }}
                        />
                        <button
                          onClick={() => confirmEdit(key)}
                          title="Confirm (Enter)"
                          style={actionBtnStyle("#e6f4ea", "#2e7d32")}
                        >✔</button>
                        <button
                          onClick={cancelEdit}
                          title="Cancel (Esc)"
                          style={actionBtnStyle("#fdecea", "#c62828")}
                        >✖</button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ flex: 1, fontSize: "14px", color: "#333" }}>
                          {String(val ?? "—")}
                        </span>
                        <button
                          onClick={() => copyToClipboard(String(val ?? ""), key)}
                          title="Copy"
                          style={actionBtnStyle("#f0f4ff", "#1a3c6e")}
                        >
                          {copied === key ? "✔" : "📋"}
                        </button>
                        <button
                          onClick={() => startEdit(key, val)}
                          title="Edit"
                          style={actionBtnStyle("#f0f4ff", "#1a3c6e")}
                        >✎</button>
                      </div>
                    )}
                  </div>
                );
              })}

            {/* Payment Terms — highlighted separately */}
            {(() => {
              const key = "Payment Terms";
              const val = viewRow[key];
              const isEditing = editCell === key;
              return (
                <div style={{
                  marginTop: "16px", background: "#f0f4ff", borderRadius: "8px",
                  padding: "16px", border: "1px solid #c0d0f0"
                }}>
                  <div style={{ fontSize: "12px", color: "#1a3c6e", fontWeight: "bold", marginBottom: "8px" }}>
                    Payment Terms
                  </div>

                  {isEditing ? (
                    <div style={{ display: "flex", gap: "6px", alignItems: "flex-start" }}>
                      <textarea
                        style={{
                          flex: 1, padding: "8px 10px", fontSize: "14px",
                          border: "1px solid #1a3c6e", borderRadius: "6px",
                          outline: "none", minHeight: "80px", resize: "vertical"
                        }}
                        value={editValue}
                        autoFocus
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.ctrlKey) confirmEdit(key);
                          if (e.key === "Escape") cancelEdit();
                        }}
                      />
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <button onClick={() => confirmEdit(key)} title="Confirm (Ctrl+Enter)" style={actionBtnStyle("#e6f4ea", "#2e7d32")}>✔</button>
                        <button onClick={cancelEdit} title="Cancel (Esc)" style={actionBtnStyle("#fdecea", "#c62828")}>✖</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                      <div style={{ flex: 1, fontSize: "14px", color: "#333", lineHeight: "1.6" }}>
                        {val || "—"}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <button
                          onClick={() => copyToClipboard(String(val ?? ""), key)}
                          title="Copy"
                          style={actionBtnStyle("#fff", "#1a3c6e")}
                        >
                          {copied === key ? "✔" : "📋"}
                        </button>
                        <button
                          onClick={() => startEdit(key, val)}
                          title="Edit"
                          style={actionBtnStyle("#fff", "#1a3c6e")}
                        >✎</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <button
              onClick={closeView}
              style={{
                marginTop: "20px", width: "100%", padding: "10px",
                background: "#1a3c6e", color: "#fff", border: "none",
                borderRadius: "8px", cursor: "pointer", fontSize: "14px"
              }}
            >Close</button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div className="table-wrapper">
        {tableLoading ? (
          <div className="table-loader">
            <span className="spinner dark"></span>
            <div className="loading-text">Loading clients...</div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                {headers.map((h) => <th key={h}>{h}</th>)}
                <th style={{ whiteSpace: "nowrap", minWidth: "60px" }}>View</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i} className={editedRows[i] ? "row-edited" : ""}>
                  {headers.map((key, j) => (
                    <td key={j}>
                      <span className="cell-text">{String(row[key] ?? "")}</span>
                    </td>
                  ))}
                  <td>
                    <button
                      className="copy-btn"
                      onClick={() => openView(row, i)}
                      title="View / Edit details"
                    >
                      {editedRows[i] ? "💾" : "👁"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* Small helper to keep button styles DRY */
function actionBtnStyle(bg, color) {
  return {
    background: bg,
    color,
    border: `1px solid ${color}22`,
    borderRadius: "6px",
    padding: "4px 8px",
    cursor: "pointer",
    fontSize: "13px",
    lineHeight: 1,
    whiteSpace: "nowrap",
    flexShrink: 0,
  };
}

export default ClientDetails;
